import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useSubscription } from "@apollo/client/react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { Button } from "@/components/design/Button";
import {
  ActOnNotificationDocument,
  CockpitNotificationsDocument,
  DeleteAllNotificationsDocument,
  DeleteNotificationDocument,
  MarkAllNotificationsReadDocument,
  NotificationEventsDocument,
  UnreadNotificationCountUpdatesDocument,
  type CockpitNotificationsQuery,
} from "@/graphql/generated/operations";
import { logApplicationFailure } from "@/lib/user-error";
import { ALL_NOTIFICATION_TYPES } from "@/lib/notification-types";

type Notification = CockpitNotificationsQuery["notifications"][number];

// Topbar notification inbox: bell + unread badge + dropdown list. The list and
// the badge update live via the notificationEvents / unreadNotificationCountUpdates
// subscriptions (BFF injects the bearer on connection_init). Each event refetches
// the inbox query so the list stays authoritative; the count subscription keeps
// the badge instant.
//
// Read model: opening the panel marks nothing. Rows stay visibly unread while
// the user reads them, and the whole inbox is marked read in one call when the
// panel closes again, by any route (outside click, Escape, the bell, following a
// deep link). Dismissing is a separate, explicit act that never happens
// implicitly.

const SUPPORTED = { supportedTypes: ALL_NOTIFICATION_TYPES };

// A bulk mark-read emits one notificationEvents delta per affected row, so the
// naive one-refetch-per-event wiring would replay the whole inbox N times.
// Debouncing collapses that burst (and any other burst) into one refetch.
const REFETCH_DEBOUNCE_MS = 150;

// How many document names a running analysis lists before the rest fold away.
const VISIBLE_DOCUMENTS = 2;

// Whether a notification may be dismissed is genesis' rule, not this client's:
// one still awaiting a decision (an unanswered contact request) cannot be
// deleted, while a pure navigation action such as "view post" does not block
// dismissal. That is what `n.deletable` carries. Re-deriving it here from
// `actions` is what hid the dismiss affordance from the two highest-volume
// notification types.
function availableActions(n: Notification) {
  return n.actions.filter((a) => a.status === "AVAILABLE");
}

export function NotificationCenter() {
  const intl = useIntl();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [confirmDismissAll, setConfirmDismissAll] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const { data, refetch } = useQuery(CockpitNotificationsDocument, {
    variables: {
      input: { take: 20, supportedTypes: ALL_NOTIFICATION_TYPES },
      supported: SUPPORTED,
    },
    fetchPolicy: "cache-and-network",
  });

  const { data: eventData } = useSubscription(NotificationEventsDocument, {
    variables: { supported: SUPPORTED },
  });
  const { data: countData } = useSubscription(UnreadNotificationCountUpdatesDocument, {
    variables: { supported: SUPPORTED },
  });

  const [dismiss] = useMutation(DeleteNotificationDocument);
  const [dismissAll] = useMutation(DeleteAllNotificationsDocument);
  const [markAllRead] = useMutation(MarkAllNotificationsReadDocument);
  const [actOn] = useMutation(ActOnNotificationDocument);

  const refetchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleRefetch = useCallback(() => {
    if (refetchTimer.current) clearTimeout(refetchTimer.current);
    refetchTimer.current = setTimeout(() => {
      refetchTimer.current = null;
      void refetch();
    }, REFETCH_DEBOUNCE_MS);
  }, [refetch]);

  // A pending refetch must not outlive the component: refetching into an
  // unmounted tree is what made the test suite need its own cleanup hook.
  useEffect(
    () => () => {
      if (refetchTimer.current) clearTimeout(refetchTimer.current);
    },
    [],
  );

  // The query is the single source of truth for the list and the badge count;
  // both subscriptions just refetch it so it stays authoritative without local
  // state syncing. unreadNotificationCount rides along on the same query.
  useEffect(() => {
    if (eventData?.notificationEvents) scheduleRefetch();
  }, [eventData, scheduleRefetch]);

  useEffect(() => {
    if (countData?.unreadNotificationCountUpdates) scheduleRefetch();
  }, [countData, scheduleRefetch]);

  const count = data?.unreadNotificationCount ?? 0;

  const notifications = (data?.notifications ?? []).filter(
    (n) => n.state === "ACTIVE" || n.state === "ACTIONED",
  );

  const hasDismissable = notifications.some((n) => n.deletable);

  // Closing is what "the panel lost focus" means here, so every close route
  // goes through this one function. markAllNotificationsRead covers the whole
  // inbox, not just the loaded page, and takes the same supportedTypes filter
  // as the badge count, so the badge provably reaches zero.
  //
  // The unread check reads `data` directly rather than a derived boolean: the
  // derived value would have to enter this callback's dependency list, which
  // costs the whole component its React Compiler memoization. A stale badge
  // count is tolerated by checking the rows too.
  const closePanel = useCallback(() => {
    setOpen(false);
    const unread =
      (data?.unreadNotificationCount ?? 0) > 0 ||
      (data?.notifications ?? []).some((n) => !n.readAt);
    if (!unread) return;
    void markAllRead({ variables: { input: SUPPORTED } })
      .then(() => scheduleRefetch())
      .catch(() => logApplicationFailure("notifications.mark-all-read"));
  }, [data, markAllRead, scheduleRefetch]);

  // While the confirm dialog is up, it owns Escape and the pointer: the native
  // <dialog> sits inside this wrapper, so letting these handlers run would tear
  // the panel (and the dialog with it) down mid-decision.
  useEffect(() => {
    if (!open || confirmDismissAll) return;
    function onDocPointerDown(event: MouseEvent) {
      if (!wrapperRef.current?.contains(event.target as Node)) closePanel();
    }
    function onDocKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") closePanel();
    }
    document.addEventListener("mousedown", onDocPointerDown);
    document.addEventListener("keydown", onDocKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocPointerDown);
      document.removeEventListener("keydown", onDocKeyDown);
    };
  }, [open, confirmDismissAll, closePanel]);

  async function onDismiss(n: Notification) {
    try {
      await dismiss({ variables: { notificationId: n.id } });
      scheduleRefetch();
    } catch {
      logApplicationFailure("notifications.dismiss");
    }
  }

  async function onDismissAll() {
    setConfirmDismissAll(false);
    try {
      await dismissAll({ variables: { input: SUPPORTED } });
      scheduleRefetch();
    } catch {
      logApplicationFailure("notifications.dismiss-all");
    }
  }

  async function onAct(n: Notification, action: Notification["actions"][number]) {
    try {
      await actOn({ variables: { input: { notificationId: n.id, action: action.type } } });
      scheduleRefetch();
    } catch {
      logApplicationFailure("notifications.action");
    }
  }

  // Only rows that actually carry a deep link are clickable, so the target is
  // handed in already narrowed rather than re-checked here.
  function onOpenNotification(deepLink: string) {
    closePanel();
    navigate(deepLink);
  }

  const label = intl.formatMessage({ id: "cockpit.notifications", defaultMessage: "Notifications" });

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        type="button"
        onClick={() => (open ? closePanel() : setOpen(true))}
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={open}
        className={`relative inline-flex h-9 w-9 items-center justify-center rounded-full text-ink/70 transition hover:bg-ink/[0.06] hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-ink/30 ${
          open ? "bg-ink/[0.06] text-ink ring-1 ring-ink/15" : ""
        }`}
      >
        <BellIcon />
        {count > 0 ? (
          <span className="absolute -right-0.5 -top-0.5 inline-flex min-w-[18px] items-center justify-center rounded-full bg-accent px-1 text-[10px] font-medium leading-[18px] text-on-accent">
            {count > 99 ? "99+" : count}
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          role="dialog"
          aria-label={label}
          className="absolute right-0 top-full z-[var(--z-dropdown)] mt-2 w-[360px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-md bg-surface shadow-lg ring-1 ring-ink/10"
        >
          <div className="flex items-center justify-between gap-2 border-b border-ink/5 px-4 py-3">
            <div className="min-w-0 truncate text-[10px] tracking-[0.25em] text-ink/55">{label}</div>
            <div className="flex shrink-0 items-center gap-2">
              {count > 0 ? (
                <span className="text-[11px] tabular-nums text-ink/55">
                  {count} <FormattedMessage id="cockpit.unread" defaultMessage="unread" />
                </span>
              ) : null}
              {hasDismissable ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="-mr-3"
                  onClick={() => setConfirmDismissAll(true)}
                >
                  <FormattedMessage id="cockpit.dismissAll" defaultMessage="Dismiss all" />
                </Button>
              ) : null}
            </div>
          </div>

          <div className="max-h-[70vh] divide-y divide-ink/5 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-[12px] text-ink/55">
                <FormattedMessage id="cockpit.noNotifications" defaultMessage="No notifications" />
              </div>
            ) : (
              notifications.map((n) => (
                <NotificationRow
                  key={n.id}
                  n={n}
                  when={n.createdAt ? intl.formatDate(n.createdAt, { dateStyle: "short", timeStyle: "short" }) : ""}
                  onOpen={onOpenNotification}
                  onDismiss={() => void onDismiss(n)}
                  onAct={(action) => void onAct(n, action)}
                />
              ))
            )}
          </div>
        </div>
      ) : null}

      <ConfirmDialog
        open={confirmDismissAll}
        title={
          <FormattedMessage id="cockpit.dismissAllTitle" defaultMessage="Dismiss all notifications?" />
        }
        message={
          <FormattedMessage
            id="cockpit.dismissAllMessage"
            defaultMessage="This clears your notification inbox and cannot be undone. Notifications that are still waiting for your answer are kept."
          />
        }
        confirmLabel={<FormattedMessage id="cockpit.dismissAll" defaultMessage="Dismiss all" />}
        confirmTestId="cockpit-dismiss-all-confirm"
        onConfirm={() => void onDismissAll()}
        onCancel={() => setConfirmDismissAll(false)}
      />
    </div>
  );
}

function NotificationRow({
  n,
  when,
  onOpen,
  onDismiss,
  onAct,
}: {
  n: Notification;
  when: string;
  onOpen: (deepLink: string) => void;
  onDismiss: () => void;
  onAct: (action: Notification["actions"][number]) => void;
}) {
  const unread = !n.readAt;
  const actions = availableActions(n);
  const deepLink = n.deepLink;
  const [showAllDocuments, setShowAllDocuments] = useState(false);

  const heading = (
    <>
      <div className="flex items-baseline justify-between gap-2">
        <span className="truncate text-[13px] font-medium text-ink">{n.title.localized || "–"}</span>
        <span className="shrink-0 text-[10px] text-ink/55">{when}</span>
      </div>
      {n.body.localized ? (
        <p className="mt-0.5 line-clamp-3 text-[12px] leading-relaxed text-ink/55">{n.body.localized}</p>
      ) : null}
    </>
  );

  const navigationContent = (
    <>
      {heading}

      {n.jobActive ? (
        <div className="mt-1.5 flex items-center gap-1.5">
          {/* Same live indicator the AI model panel uses, so "something is running" reads the
              same way everywhere in the product. */}
          <span className="pulse-dot h-[7px] w-[7px] shrink-0 rounded-full bg-accent" aria-hidden="true" />
          <span className="text-[10px] tracking-[0.12em] text-ink/55">
            <FormattedMessage id="cockpit.jobActive" defaultMessage="Active" />
          </span>
        </div>
      ) : null}

      {n.progress ? (
        // A batch notification updates in place (UPDATED events refetch this
        // list), so the bar moves live. The body already carries the numbers
        // as text; the bar is the shape a tally is actually read in.
        <div
          className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-ink/10"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={n.progress.total}
          aria-valuenow={n.progress.done}
        >
          <div
            className="h-full rounded-full bg-accent transition-[width] duration-300"
            style={{
              width: `${n.progress.total > 0 ? Math.min(100, (n.progress.done / n.progress.total) * 100) : 0}%`,
            }}
          />
        </div>
      ) : null}
    </>
  );

  // Long file lists would push the rest of the inbox out of view, so only the first few are shown
  // until asked. Expanding is one-way: nobody opens a list in order to close it again.
  const shownDocuments = showAllDocuments ? n.documentNames : n.documentNames.slice(0, VISIBLE_DOCUMENTS);
  const hiddenDocumentCount = n.documentNames.length - shownDocuments.length;

  return (
    <div className={`flex gap-2 px-4 py-3 ${unread ? "bg-accent/[0.04]" : ""}`}>
      <span
        aria-hidden="true"
        className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${unread ? "bg-accent" : "bg-transparent"}`}
      />
      <div className="min-w-0 flex-1">
        {deepLink ? (
          <button
            type="button"
            onClick={() => onOpen(deepLink)}
            className="-m-1 block w-[calc(100%+0.5rem)] cursor-pointer rounded p-1 text-left transition hover:bg-ink/[0.04] focus:outline-none focus-visible:ring-2 focus-visible:ring-ink/30"
          >
            {navigationContent}
          </button>
        ) : (
          navigationContent
        )}

        {n.documentNames.length > 0 ? (
          <ul className="mt-1 space-y-0.5">
            {shownDocuments.map((name, index) => (
              <li key={`${name}-${index}`} className="truncate text-[11px] text-ink/55">
                {name}
              </li>
            ))}
            {hiddenDocumentCount > 0 ? (
              <li>
                <Button
                  variant="ghost"
                  size="sm"
                  className="-ml-3"
                  onClick={() => setShowAllDocuments(true)}
                >
                  <FormattedMessage
                    id="cockpit.showMoreDocuments"
                    defaultMessage="Show {count} more"
                    values={{ count: hiddenDocumentCount }}
                  />
                </Button>
              </li>
            ) : null}
          </ul>
        ) : null}

        {actions.length > 0 ? (
          <div className="mt-2 flex flex-wrap gap-2">
            {actions.map((a) => (
              <Button
                key={a.type}
                variant="secondary"
                size="sm"
                onClick={() => onAct(a)}
              >
                {a.label.localized || a.type}
              </Button>
            ))}
          </div>
        ) : null}

        {n.deletable ? (
          <div className="mt-1.5 flex items-center">
            <Button variant="ghost" size="sm" className="-ml-3" onClick={onDismiss}>
              <FormattedMessage id="cockpit.dismiss" defaultMessage="Dismiss" />
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function BellIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0"
      />
    </svg>
  );
}
